import type { DmcProfileFull } from '../schemas/organization-dmc-profile';
import { dmcResponsibilityDomainValues } from '../schemas/organization-dmc-profile';
import { summarizeDmcExpiries } from './dmc-expiry';

// ---------------------------------------------------------------------------
// Eleggia DMC Partner Profile — data-quality assessment (PRD FR-08, read-only).
//
// Pure derivation over an already-loaded `DmcProfileFull`: no DB, no I/O, no
// writes. Encodes the cross-record *completeness* rules from PRD §8 (which
// describe a "complete" profile, distinct from the per-field syntactic
// validation Zod already enforces on save), folds in the expired-document count
// from `summarizeDmcExpiries`, and reports staleness from the parent's
// `modifiedOn` timestamp.
//
// The spec references a per-field "update frequency" for fine-grained staleness,
// but that metadata is not modelled on the schema — so staleness here is a
// coarse whole-profile signal (last-modified age), not per-field. When the
// frequency registry lands, extend the staleness section rather than the rules.
// ---------------------------------------------------------------------------

// Whole-profile "stale" threshold — flagged when the profile has not been
// touched in this many days. Coarse proxy until per-field frequency exists.
export const DMC_STALE_PROFILE_DAYS = 180;

export type DmcQualitySeverity = 'error' | 'warning';

// A single completeness / staleness finding.
export type DmcQualityIssue = {
  // Stable machine key so the UI can dedupe / test against it.
  code: string;
  severity: DmcQualitySeverity;
  // Which module the rule belongs to (for grouping in the UI).
  module: string;
  message: string;
};

export type DmcQualityReport = {
  issues: DmcQualityIssue[];
  errorCount: number;
  warningCount: number;
  // Completeness = required rules satisfied / total required rules, 0..100.
  // Only the `error`-severity (required) rules count toward the score; warnings
  // (staleness, expired docs) are surfaced but don't lower completeness.
  completeness: number;
};

// UTC-midnight day diff (reference → target). Positive when target is later.
const utcDaysBetween = (fromIso: string, toIso: string): number | null => {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  const fromMid = Date.UTC(
    from.getUTCFullYear(),
    from.getUTCMonth(),
    from.getUTCDate()
  );
  const toMid = Date.UTC(
    to.getUTCFullYear(),
    to.getUTCMonth(),
    to.getUTCDate()
  );
  return Math.round((toMid - fromMid) / (1000 * 60 * 60 * 24));
};

// A required completeness rule (PRD §8). `ok` returns whether the profile
// satisfies it; a failure becomes an `error`-severity issue and counts against
// the completeness score.
type RequiredRule = {
  code: string;
  module: string;
  message: string;
  ok: (p: DmcProfileFull) => boolean;
};

const REQUIRED_RULES: RequiredRule[] = [
  {
    code: 'office-min-one',
    module: 'Offices',
    message: 'At least one office is required.',
    ok: (p) => p.offices.length > 0,
  },
  {
    code: 'team-min-one',
    module: 'Team',
    message: 'At least one team member is required.',
    ok: (p) => p.team.length > 0,
  },
  {
    code: 'insurance-public-liability',
    module: 'Insurance',
    message: 'A Public Liability insurance policy is required.',
    ok: (p) =>
      p.insurancePolicies.some((i) => i.policyType === 'Public Liability'),
  },
  {
    code: 'bank-primary',
    module: 'Banking',
    message: 'One bank account must be marked primary.',
    ok: (p) => p.bankAccounts.some((b) => b.isPrimary),
  },
  {
    code: 'responsibility-owners',
    module: 'Responsibility',
    message: 'Each responsibility domain needs exactly one named owner.',
    ok: (p) =>
      dmcResponsibilityDomainValues.every((d) => {
        const owner = p.responsibilityStructure[d];
        return typeof owner === 'string' && owner.trim().length > 0;
      }),
  },
  {
    code: 'crisis-team-min-two',
    module: 'Team',
    message: 'The crisis team must have at least two members.',
    ok: (p) => p.team.filter((m) => m.isCrisisTeam).length >= 2,
  },
];

// Assess a loaded profile. `referenceIso` ("now") is passed in so the function
// stays pure and deterministic (no ambient Date.now()), matching the repo rule.
export const assessDmcDataQuality = (
  profile: DmcProfileFull,
  referenceIso: string
): DmcQualityReport => {
  const issues: DmcQualityIssue[] = [];

  // 1. Required completeness rules (PRD §8) — each failure is an error.
  let satisfied = 0;
  for (const rule of REQUIRED_RULES) {
    if (rule.ok(profile)) {
      satisfied++;
    } else {
      issues.push({
        code: rule.code,
        severity: 'error',
        module: rule.module,
        message: rule.message,
      });
    }
  }

  // 2. Referential rule — a client cannot reference a market absent from
  //    module 08. One issue per dangling client (a warning: the record exists
  //    but points nowhere, rather than a missing-required-record).
  const marketIds = new Set(profile.sourceMarkets.map((m) => m.id));
  for (const client of profile.clients) {
    if (client.marketRef && !marketIds.has(client.marketRef)) {
      issues.push({
        code: `client-market-missing:${client.id}`,
        severity: 'warning',
        module: 'Clients',
        message: `Client "${client.name ?? 'Unnamed'}" references a market not in Source Markets.`,
      });
    }
  }

  // 3. Expired documents — reuse the expiry summary; every expired item is a
  //    data-quality warning (already visible in the expiry panel, but folded in
  //    here so the dashboard is a single source of "what's wrong").
  const expiry = summarizeDmcExpiries(profile, referenceIso);
  if (expiry.expiredCount > 0) {
    issues.push({
      code: 'expired-items',
      severity: 'warning',
      module: 'Compliance',
      message: `${expiry.expiredCount} item${expiry.expiredCount === 1 ? ' is' : 's are'} expired.`,
    });
  }

  // 4. Staleness — coarse whole-profile signal from `modifiedOn`.
  if (profile.modifiedOn) {
    const age = utcDaysBetween(profile.modifiedOn, referenceIso);
    if (age !== null && age >= DMC_STALE_PROFILE_DAYS) {
      issues.push({
        code: 'profile-stale',
        severity: 'warning',
        module: 'Profile',
        message: `Profile has not been updated in ${age} days.`,
      });
    }
  }

  const errorCount = issues.filter((i) => i.severity === 'error').length;
  const warningCount = issues.filter((i) => i.severity === 'warning').length;
  const completeness = Math.round((satisfied / REQUIRED_RULES.length) * 100);

  return { issues, errorCount, warningCount, completeness };
};
